import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple, CheckCircle2, Plus, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import type { CropLoadList } from '../../types/orchard';
import { fmt, STAGE_LABEL } from '../../utils/cropLoad';
import CropLoadResultView from './CropLoadResultView';

/**
 * Crop load counts on a cultivar area: sample trees measured and counted,
 * worked out to fruit, tonnes and bins. The latest is shown in full; a Farm
 * Jobs creator or admin can make one the season's crop estimate, which is
 * what Insights → Harvest progress measures picking against.
 */
export default function CropLoadPanel({ areaId }: { areaId: number }) {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [data, setData] = useState<CropLoadList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await orchardService.getCropLoads(areaId);
      if (res.success && res.data) setData(res.data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the crop load counts'));
    }
  }, [areaId]);
  useEffect(() => { load(); }, [load]);

  const apply = async (countId: number) => {
    setApplying(countId);
    setMessage(null);
    try {
      const res = await orchardService.applyCropLoad(countId);
      if (res.success && res.data) {
        setMessage(`Crop estimate for ${res.data.season_year}–${String(res.data.season_year + 1).slice(2)} set to ${fmt(res.data.result.bins)} bins.`);
      }
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not set the crop estimate'));
    } finally {
      setApplying(null);
    }
  };

  const newCount = () => navigate(`/orchard/cultivar-areas/${areaId}/crop-load/new`);
  const latest = data?.counts[0] ?? null;
  const earlier = data?.counts.slice(1) ?? [];

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Apple className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-700">Crop load counts</h3>
              <p className="text-xs text-neutral-500">
                Measure the trunk of sample trees and count their fruit. The count is scaled to every tree in the area.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={newCount}>
            <Plus className="h-4 w-4" />
            New count
          </Button>
        </div>

        {error && <p className="text-sm text-error-700">{error}</p>}
        {message && <p className="text-sm text-green-700">{message}</p>}

        {!data && !error && (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 text-neutral-400 animate-spin" /></div>
        )}

        {data && !latest && (
          <p className="text-sm text-neutral-500">
            No counts yet. Count at least five trees spread across the area, each with its trunk measured.
          </p>
        )}

        {latest && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-neutral-800">
                {formatLocalDateInTz(latest.counted_on, timezone)}
                {latest.stage && <span className="text-neutral-500 font-normal"> · {STAGE_LABEL[latest.stage]}</span>}
                {latest.created_by_name && <span className="text-neutral-500 font-normal"> · {latest.created_by_name}</span>}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {latest.applied_at && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Crop estimate since {formatDateInTz(latest.applied_at, timezone)}
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={() => navigate(`/orchard/cultivar-areas/${areaId}/crop-load/${latest.id}`)}>
                  Open
                </Button>
                {data?.can_apply && (latest.result.bins != null || latest.result.tonnes != null) && (
                  <Button size="sm" variant="primary" isLoading={applying === latest.id} onClick={() => apply(latest.id)}>
                    {latest.applied_at ? 'Use again' : 'Use as crop estimate'}
                  </Button>
                )}
              </div>
            </div>
            <CropLoadResultView result={latest.result} />
          </div>
        )}

        {earlier.length > 0 && (
          <div className="border-t border-neutral-100 pt-3">
            <p className="text-xs font-medium text-neutral-500 mb-2">Earlier counts</p>
            <ul className="divide-y divide-neutral-100">
              {earlier.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full text-left py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm hover:bg-neutral-50 rounded"
                    onClick={() => navigate(`/orchard/cultivar-areas/${areaId}/crop-load/${c.id}`)}
                  >
                    <span className="text-neutral-800">
                      {formatLocalDateInTz(c.counted_on, timezone)}
                      {c.stage && <span className="text-neutral-500"> · {STAGE_LABEL[c.stage]}</span>}
                    </span>
                    <span className="text-neutral-600">
                      {c.result.samples_counted} trees · {fmt(c.result.fruit_per_tree)} a tree · {fmt(c.result.bins)} bins
                      {c.applied_at && <span className="text-green-700"> · used</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
